import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import Generator, { type GeneratorRenderProps } from 'modules/reports/components/Generator'
import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import type ReportStruct from 'modules/reports/lib/ReportStruct'
import { ReportEditorStore } from 'modules/reports/lib/Store'
import reportFromTemplate from 'modules/reports/lib/Store/reportFromTemplate'
import conditions from 'modules/reports/store/collections/conditions'
import filters from 'modules/reports/store/collections/filters'
import projections from 'modules/reports/store/collections/projections'
import reports from 'modules/reports/store/collections/reports'
import templates from 'modules/reports/store/collections/templates'
import type Dashboard from 'modules/reports/store/models/Dashboard'
import Report from 'modules/reports/store/models/Report'
import type Template from 'modules/reports/store/models/Template'

import { Content } from './Content'
import { Header } from './Header'

interface Props {
  reportStruct: ReportStruct<Template>
  dashboard: Dashboard
}

const Item = ({ reportStruct, dashboard }: Props) => {
  const [loadingRequest, setLoadingRequest] = React.useState<boolean>(false)
  const template = templates.bySlug(reportStruct.templateSlug)
  const report = template ? reportFromTemplate(template, dashboard) : new Report({})
  const store = React.useRef(
    new ReportEditorStore({
      report,
    })
  ).current
  const { track } = useReportTracking(store)

  React.useEffect(() => {
    store.start()
  }, [store])

  const onSaveTemplate = async () => {
    setLoadingRequest(true)

    const { success } = await asyncAction(
      async () => {
        report.rpc('save', store.serializeForSave('template'))
      },
      {
        successMessage: i18n.t('custom_reports.dashboard.assign_dashboard.widget_saved', {
          dashboardName: dashboard.title,
        }),
      }
    )

    if (success) {
      store.saved = true
      track(trackingEvents.reports.newReportFromTemplateSaved)
      track(trackingEvents.reports.submitReportFromLibrary, {
        slug: reportStruct.templateSlug,
      })
      await Promise.all([projections.fetch(), filters.fetch(), conditions.fetch(), reports.fetch()])
    }

    setLoadingRequest(false)
  }

  return (
    <ErrorBoundary>
      <Generator reportStruct={reportStruct} variables={[]}>
        {({ data, error, loading }: GeneratorRenderProps) => {
          return (
            <Box
              flexDirection="column"
              width="full"
              height="full"
              border={{
                all: {
                  color: 'grey400',
                  width: 's1',
                  style: 'solid',
                },
              }}
              borderRadius={{
                all: 'abs004',
              }}
            >
              <Header
                reportStruct={reportStruct}
                onSaveTemplate={onSaveTemplate}
                store={store}
                loading={loadingRequest}
              />
              <Content reportStruct={reportStruct} loading={loading} data={data} error={error} />
            </Box>
          )
        }}
      </Generator>
    </ErrorBoundary>
  )
}

export default observer(Item)
